import { MULTIPLAYER_PLAYER_SNAKE_SYSTEM_VERSION } from '../engine/multiplayer/MultiplayerPlayerSnakeSystem.js';

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

export function installMultiplayerPlayerSnakesTestApi({
  baseApi,
  system,
  integration,
  bus,
  simulationSnapshots,
  windowRef = globalThis.window,
}) {
  if (!baseApi || typeof baseApi !== 'object') throw new TypeError('baseApi must be an object');
  for (const method of ['getStatus', 'getDiagnostics', 'getPlayerSnake', 'listSnakes', 'snapshot', 'restore', 'reset']) {
    assertMethod(system, method, 'system');
  }
  assertMethod(integration, 'syncNow', 'integration');
  assertMethod(integration, 'renderNow', 'integration');
  assertMethod(integration, 'focusPlayer', 'integration');
  assertMethod(bus, 'emit', 'bus');
  assertMethod(simulationSnapshots, 'getStatus', 'simulationSnapshots');

  const baseCaptureSnapshot = typeof baseApi.captureSnapshot === 'function'
    ? baseApi.captureSnapshot.bind(baseApi)
    : null;

  const emitChange = (operation, value) => {
    const status = system.getStatus();
    bus.emit('multiplayer-player-snakes:changed', { operation, value, status });
    return status;
  };

  const api = Object.freeze({
    ...baseApi,
    multiplayerPlayerSnakeSystemVersion: MULTIPLAYER_PLAYER_SNAKE_SYSTEM_VERSION,
    getMultiplayerPlayerSnakeStatus: () => system.getStatus(),
    getMultiplayerPlayerSnakeDiagnostics: () => system.getDiagnostics(),
    getMultiplayerPlayerSnake: (playerId) => system.getPlayerSnake(playerId),
    listMultiplayerPlayerSnakes: () => system.listSnakes(),
    syncMultiplayerPlayerSnakes: () => {
      const status = integration.syncNow();
      return { ok: true, status: emitChange('sync', status) };
    },
    focusMultiplayerPlayerSnake: (playerId) => ({
      ok: true,
      playerId,
      camera: integration.focusPlayer(playerId),
    }),
    renderMultiplayerPlayerSnakesFrame: () => ({
      ok: true,
      rendering: integration.renderNow(),
    }),
    captureMultiplayerPlayerSnakeSnapshot: () => system.snapshot(),
    exportMultiplayerPlayerSnakeSnapshot: () => system.exportSnapshot(),
    restoreMultiplayerPlayerSnakeSnapshot: (snapshot) => {
      const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
      const nextTick = simulationSnapshots.getStatus().nextTick;
      if (parsed?.tick !== null && parsed?.tick !== undefined && parsed.tick >= nextTick) {
        throw new RangeError('Multiplayer player-snake snapshot must precede the next simulation tick');
      }
      const state = system.restore(snapshot);
      integration.renderNow();
      bus.emit('multiplayer-player-snakes:restore', state);
      return { ok: true, state, status: emitChange('restore', state) };
    },
    resetMultiplayerPlayerSnakes: (options = {}) => {
      const status = system.reset(options);
      integration.renderNow();
      bus.emit('multiplayer-player-snakes:reset', status);
      return { ok: true, status: emitChange('reset', status) };
    },
    captureSnapshot: baseCaptureSnapshot === null
      ? undefined
      : () => ({
        ...baseCaptureSnapshot(),
        multiplayerPlayerSnakes: system.snapshot(),
        multiplayerPlayerSnakesRendering: integration.getStatus(),
      }),
  });

  Object.defineProperty(windowRef, '__SERPENT_TEST__', {
    configurable: true,
    enumerable: false,
    value: api,
  });
  return api;
}
